/**
 * 费用分摊计算器工具函数
 */

/**
 * 计算费用分摊
 * @param {Object} form - 表单数据
 * @returns {Array} 分摊结果列表
 */
export function calculateCost(form) {
  // 自动计算总金额（如果用户未输入）
  const totalAmount = form.totalAmount > 0 ? form.totalAmount :
    form.personList.reduce((sum, person) => sum + person.payAmount, 0);

  // 基础分摊计算 - 使用深拷贝避免修改原始表单对象
  let resultList = form.personList.map(person => {
    // 个人应付金额 = 总金额 / 参与人数量（人均平摊）
    const shouldPay = totalAmount / form.personList.length;

    // 差额计算：个人差额 = 个人实付金额 - 个人应付金额
    // 正数表示应退，负数表示应缴
    const diffAmount = person.payAmount - shouldPay;

    // 创建全新对象，避免引用原始表单对象
    return {
      name: person.name,
      payAmount: person.payAmount,
      shouldPay: shouldPay,
      diffAmount: diffAmount,
      finalDiff: diffAmount,
      type: diffAmount >= 0 ? "应退" : "应缴",
      finalAmount: Math.abs(diffAmount)
    };
  });

  // 代付项调整
  form.proxyList.forEach(proxyItem => {
    // 校验代付项是否有效（姓名非空、金额>0）
    if (proxyItem.proxyName.trim() && proxyItem.beProxyName.trim() && proxyItem.proxyAmount > 0) {
      // 查找代付人
      const proxyPerson = resultList.find(item => item.name === proxyItem.proxyName.trim());
      // 查找被代付人
      const beProxyPerson = resultList.find(item => item.name === proxyItem.beProxyName.trim());

      if (proxyPerson && beProxyPerson) {
        // 调整最终差额：代付人finalDiff += 代付金额；被代付人finalDiff -= 代付金额
        proxyPerson.finalDiff += proxyItem.proxyAmount;
        beProxyPerson.finalDiff -= proxyItem.proxyAmount;

        // 更新类型和最终金额
        proxyPerson.type = proxyPerson.finalDiff >= 0 ? "应退" : "应缴";
        proxyPerson.finalAmount = Math.abs(proxyPerson.finalDiff);
        beProxyPerson.type = beProxyPerson.finalDiff >= 0 ? "应退" : "应缴";
        beProxyPerson.finalAmount = Math.abs(beProxyPerson.finalDiff);
      }
    }
  });

  // 结果格式化：保留2位小数
  return resultList.map(item => ({
    ...item,
    payAmount: parseFloat(item.payAmount.toFixed(2)),
    shouldPay: parseFloat(item.shouldPay.toFixed(2)),
    diffAmount: parseFloat(item.diffAmount.toFixed(2)),
    finalDiff: parseFloat(item.finalDiff.toFixed(2)),
    finalAmount: parseFloat(item.finalAmount.toFixed(2))
  }));
}

/**
 * 验证表单数据
 * @param {Object} form - 表单数据
 * @returns {Object} 验证结果 { valid: boolean, message: string }
 */
export function validateForm(form) {
  // 前置校验1：参与人数量校验
  if (form.personList.length < 1) {
    return { valid: false, message: "至少需要1个参与人！" };
  }

  // 前置校验2：参与人姓名校验
  if (form.personList.some(item => !item.name.trim())) {
    return { valid: false, message: "所有参与人姓名不能为空！" };
  }

  // 前置校验3：总金额校验
  const totalAmount = form.totalAmount > 0 ? form.totalAmount :
    form.personList.reduce((sum, person) => sum + person.payAmount, 0);
  if (totalAmount <= 0) {
    return { valid: false, message: "总金额必须大于0！" };
  }

  return { valid: true, message: "" };
}

/**
 * 生成支付建议
 * @param {Array} resultList - 分摊结果列表
 * @returns {Array} 支付建议列表
 */
export function generatePaymentSuggestions(resultList) {
  // 深拷贝结果列表，避免修改原始数据
  const clonedResultList = JSON.parse(JSON.stringify(resultList));

  // 分离应退和应缴人员
  const refundPeople = clonedResultList.filter(person => person.finalDiff > 0);
  const payablePeople = clonedResultList.filter(person => person.finalDiff < 0);

  const suggestions = [];
  let refundIndex = 0;
  let payableIndex = 0;

  // 简化的支付建议算法：应缴人员按顺序向应退人员支付
  while (refundIndex < refundPeople.length && payableIndex < payablePeople.length) {
    const refundPerson = refundPeople[refundIndex];
    const payablePerson = payablePeople[payableIndex];

    // 计算支付金额（取较小值）
    const paymentAmount = Math.min(refundPerson.finalDiff, Math.abs(payablePerson.finalDiff));

    // 添加支付建议
    suggestions.push({
      from: payablePerson.name,
      to: refundPerson.name,
      amount: parseFloat(paymentAmount.toFixed(2))
    });

    // 更新剩余金额（仅修改拷贝后的数据，不影响原始数据）
    refundPerson.finalDiff -= paymentAmount;
    payablePerson.finalDiff += paymentAmount;

    // 如果某一方的剩余金额为0，移动到下一个人
    if (refundPerson.finalDiff <= 0.01) {
      refundIndex++;
    }
    if (Math.abs(payablePerson.finalDiff) <= 0.01) {
      payableIndex++;
    }
  }

  return suggestions;
}

import { calculateWithDiscount } from './discount-engine';
import { calculateTopUpSuggestions } from './suggestion';

/**
 * 统一计算入口：根据模式分发到不同计算器
 * @param {Object} form
 * @param {string} mode - 'simple' | 'discount'
 * @returns {{ resultList: Array, discountSummary?: Object, suggestionData?: Object }}
 */
export function computeResult(form, mode = 'simple') {
  if (mode === 'discount' && form.discountRules && form.discountRules.enabled) {
    const discountResult = calculateWithDiscount(form);
    const rules = form.discountRules;
    const thresholds = (rules.thresholds || []).filter(t => t.threshold > 0 && t.discount > 0);

    const suggestionData = thresholds.length > 0
      ? calculateTopUpSuggestions(
          discountResult.discountSummary.totalOriginal,
          thresholds,
          form.personList
        )
      : null;

    return {
      resultList: discountResult.resultList,
      discountSummary: discountResult.discountSummary,
      suggestionData
    };
  }

  return {
    resultList: calculateCost(form)
  };
}
